import { createSelector } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

// ============================================================================
// Base Selectors
// ============================================================================

const selectEditState = (state: RootState) => state.dashboardEdit;

export const selectIsInitialized = createSelector(selectEditState, state => state.isInitialized);

export const selectIsDirty = createSelector(selectEditState, state => state.isDirty);

export const selectDashboardName = createSelector(selectEditState, state => state.dashboardName);

export const selectInitialDashboardName = createSelector(
  selectEditState,
  state => state.initialDashboardName
);

export const selectDraftTabs = createSelector(selectEditState, state => state.draftTabs);

export const selectCurrentTabId = createSelector(selectEditState, state => state.currentTabId);

export const selectTabDrafts = createSelector(selectEditState, state => state.tabDrafts);

export const selectOtherDashboardDrafts = createSelector(
  selectEditState,
  state => state.otherDashboardDrafts
);

export const selectDirtyDashboards = createSelector(
  selectEditState,
  state => state.dirtyDashboards
);

// ============================================================================
// Derived Selectors
// ============================================================================

export const selectCurrentTab = createSelector(
  [selectDraftTabs, selectCurrentTabId],
  (tabs, currentId) => tabs.find(t => t.id === currentId)
);

export const selectCurrentTabName = createSelector(selectCurrentTab, tab => tab?.name ?? '');

export const selectDraftTabNames = createSelector(selectDraftTabs, tabs => tabs.map(t => t.name));

export const selectTabSourceNames = createSelector(selectDraftTabs, tabs =>
  tabs.map(t => t.sourceName)
);

export const selectCurrentTabIndex = createSelector(
  [selectDraftTabs, selectCurrentTabId],
  (tabs, currentId) => {
    if (!currentId) return 0;
    const index = tabs.findIndex(t => t.id === currentId);
    return index === -1 ? 0 : index;
  }
);

// ============================================================================
// Current Tab Content
// ============================================================================

export const selectCurrentTabDraft = createSelector(
  [selectTabDrafts, selectCurrentTabId],
  (tabDrafts, currentId) => {
    if (!currentId) return null;
    return tabDrafts[currentId] ?? null;
  }
);

export const selectCurrentMarketTabApps = createSelector(
  selectCurrentTabDraft,
  draft => draft?.marketTabApps ?? []
);

export const selectCurrentTiles = createSelector(
  selectCurrentTabDraft,
  draft => draft?.tiles ?? []
);

// ============================================================================
// Tab Draft by ID
// ============================================================================

export const selectTabDraftById = (tabId: string) =>
  createSelector(selectTabDrafts, tabDrafts => tabDrafts[tabId] ?? null);

// ============================================================================
// Validation State
// ============================================================================

export const selectHasChanges = createSelector(
  [selectIsDirty, selectDashboardName, selectInitialDashboardName],
  (isDirty, current, initial) => isDirty || current !== initial
);

export const selectCanSave = createSelector(
  [selectHasChanges, selectIsInitialized],
  (hasChanges, isInit) => hasChanges && isInit
);

// ============================================================================
// Tab-specific Derived Selectors
// ============================================================================

export const selectHasTabOrderChanges = (initialTabNames: string[]) =>
  createSelector([selectDraftTabNames], draftNames => {
    if (draftNames.length !== initialTabNames.length) return true;
    return draftNames.some((name, i) => name !== initialTabNames[i]);
  });

export const selectHasNewTabs = createSelector([selectDraftTabs], tabs =>
  tabs.some(tab => tab.sourceName === null)
);

export const selectHasDuplicateTabNames = createSelector([selectDraftTabNames], names => {
  const trimmed = names.map(n => n.trim().toLowerCase());
  return new Set(trimmed).size !== trimmed.length;
});

export const selectHasAppTabNameErrors = createSelector(
  [selectDraftTabs, selectTabDrafts],
  (draftTabs, tabDrafts) => {
    return draftTabs.some(draftTab => {
      const draft = tabDrafts[draftTab.id];
      const apps = draft?.marketTabApps ?? [];
      return apps.some(app => app.tabs.some(tab => !tab.name || tab.name.trim() === ''));
    });
  }
);

export const selectHasTabNameErrors = createSelector(
  [selectHasAppTabNameErrors, selectDraftTabs, selectDraftTabNames],
  (hasAppTabErrors, draftTabs, draftNames) => {
    // Basic check for empty header tab names
    const hasEmptyHeaderTab = draftTabs.some(tab => !tab.name || tab.name.trim() === '');

    // Check for duplicates
    const trimmed = draftNames.map(n => n.trim().toLowerCase());
    const hasDuplicates = new Set(trimmed).size !== trimmed.length;

    // Check for invalid characters (sync with createNameSchema)

    const hasInvalidChars = draftNames.some(
      // eslint-disable-next-line no-control-regex
      name => /[?#%;]/u.test(name) || name === '.' || name === '..' || /[\x00-\x1F\x7F]/.test(name)
    );

    return hasAppTabErrors || hasEmptyHeaderTab || hasDuplicates || hasInvalidChars;
  }
);

// ============================================================================
// UI State
// ============================================================================

export const selectInsertPosition = createSelector(selectEditState, state => state.insertPosition);

export const selectInsertAfterAppId = createSelector(
  selectInsertPosition,
  position => position?.afterId ?? null
);

// ============================================================================
// Complete State (for save operations)
// ============================================================================

export const selectEditStateForSave = createSelector(selectEditState, state => state);

// ============================================================================
// Draft Consistency Checks
// ============================================================================

/**
 * Returns the allDashboardsData from the edit state.
 * Used to check if dashboard data is available in cache.
 */
export const selectAllDashboardsData = createSelector(
  selectEditState,
  state => state.allDashboardsData
);

/**
 * Returns true if the current draft is stale (doesn't match the expected dashboard).
 * Used to detect when a re-initialization is needed after dashboard switching.
 */
export const selectIsDraftStale = (expectedDashboardName: string) =>
  createSelector(
    [selectIsInitialized, selectInitialDashboardName],
    (isInitialized, initialName) => {
      if (!isInitialized) return true;
      return initialName !== expectedDashboardName;
    }
  );

/**
 * Returns true if the dashboard exists in allDashboardsData cache.
 */
export const selectDashboardExistsInCache = (dashboardName: string) =>
  createSelector(selectAllDashboardsData, allDashboardsData => !!allDashboardsData[dashboardName]);
